import type { MatrixClient } from 'matrix-js-sdk';
import { CryptoEvent } from 'matrix-js-sdk/lib/crypto-api/CryptoEvent.js';
import {
  VerificationPhase,
  VerificationRequestEvent,
  VerifierEvent,
  type ShowSasCallbacks,
  type VerificationRequest,
  type Verifier,
} from 'matrix-js-sdk/lib/crypto-api/verification.js';
import { useSyncExternalStore } from 'react';

/**
 * Verificação entre aparelhos (T2.2.3): os dois lados comparam sete desenhos
 * (SAS por emoji) na linguagem do produto. Vale para outro aparelho meu e
 * para um contato, dentro da conversa protegida.
 */
export type VerificationStage =
  'idle' | 'incoming' | 'waiting' | 'emoji' | 'done' | 'cancelled' | 'error';

export type VerificationState = {
  stage: VerificationStage;
  isSelf: boolean;
  otherUserId: string | null;
  emoji: { symbol: string; name: string }[];
  error: string;
  busy: boolean;
};

const IDLE: VerificationState = {
  stage: 'idle',
  isSelf: true,
  otherUserId: null,
  emoji: [],
  error: '',
  busy: false,
};

// Nomes oficiais dos 64 desenhos do protocolo, em português.
const EMOJI_PT: Record<string, string> = {
  Dog: 'Cachorro',
  Cat: 'Gato',
  Lion: 'Leão',
  Horse: 'Cavalo',
  Unicorn: 'Unicórnio',
  Pig: 'Porco',
  Elephant: 'Elefante',
  Rabbit: 'Coelho',
  Panda: 'Panda',
  Rooster: 'Galo',
  Penguin: 'Pinguim',
  Turtle: 'Tartaruga',
  Fish: 'Peixe',
  Octopus: 'Polvo',
  Butterfly: 'Borboleta',
  Flower: 'Flor',
  Tree: 'Árvore',
  Cactus: 'Cacto',
  Mushroom: 'Cogumelo',
  Globe: 'Globo',
  Moon: 'Lua',
  Cloud: 'Nuvem',
  Fire: 'Fogo',
  Banana: 'Banana',
  Apple: 'Maçã',
  Strawberry: 'Morango',
  Corn: 'Milho',
  Pizza: 'Pizza',
  Cake: 'Bolo',
  Heart: 'Coração',
  Smiley: 'Sorriso',
  Robot: 'Robô',
  Hat: 'Chapéu',
  Glasses: 'Óculos',
  Spanner: 'Chave inglesa',
  Santa: 'Papai Noel',
  'Thumbs Up': 'Joinha',
  Umbrella: 'Guarda-chuva',
  Hourglass: 'Ampulheta',
  Clock: 'Relógio',
  Gift: 'Presente',
  'Light Bulb': 'Lâmpada',
  Book: 'Livro',
  Pencil: 'Lápis',
  Paperclip: 'Clipe',
  Scissors: 'Tesoura',
  Lock: 'Cadeado',
  Key: 'Chave',
  Hammer: 'Martelo',
  Telephone: 'Telefone',
  Flag: 'Bandeira',
  Train: 'Trem',
  Bicycle: 'Bicicleta',
  Aeroplane: 'Avião',
  Rocket: 'Foguete',
  Trophy: 'Troféu',
  Ball: 'Bola',
  Guitar: 'Violão',
  Trumpet: 'Trompete',
  Bell: 'Sino',
  Anchor: 'Âncora',
  Headphones: 'Fones',
  Folder: 'Pasta',
  Pin: 'Alfinete',
};

let state: VerificationState = IDLE;
let request: VerificationRequest | null = null;
let verifier: Verifier | null = null;
let sas: ShowSasCallbacks | null = null;
const listeners = new Set<() => void>();

function set(patch: Partial<VerificationState>) {
  state = { ...state, ...patch };
  for (const listener of listeners) listener();
}

function detach() {
  request?.off(VerificationRequestEvent.Change, onRequestChange);
  request = null;
  verifier = null;
  sas = null;
}

function bindVerifier(next: Verifier) {
  if (verifier === next) return;
  verifier = next;
  next.on(VerifierEvent.ShowSas, (callbacks) => {
    sas = callbacks;
    set({
      stage: 'emoji',
      busy: false,
      emoji: (callbacks.sas.emoji ?? []).map(([symbol, name]) => ({
        symbol,
        name: EMOJI_PT[name] ?? name,
      })),
    });
  });
  next
    .verify()
    .then(() => set({ stage: 'done', busy: false }))
    .catch(() => {
      if (state.stage !== 'done' && state.stage !== 'cancelled') {
        set({
          stage: 'cancelled',
          busy: false,
          error: state.error || 'A confirmação foi interrompida do outro lado.',
        });
      }
    });
}

function onRequestChange() {
  if (!request) return;
  if (import.meta.env.DEV) {
    console.info(
      `[verificacao] ${JSON.stringify({
        phase: request.phase,
        initiatedByMe: request.initiatedByMe,
        verifier: !!request.verifier,
        otherDevice: request.otherDeviceId,
      })}`,
    );
  }
  if (request.phase === VerificationPhase.Cancelled) {
    set({
      stage: 'cancelled',
      busy: false,
      error: state.error || 'A confirmação foi cancelada.',
    });
    return;
  }
  if (request.phase === VerificationPhase.Done) {
    set({ stage: 'done', busy: false });
    return;
  }
  if (request.verifier) {
    bindVerifier(request.verifier);
    return;
  }
  if (request.phase === VerificationPhase.Ready && request.initiatedByMe) {
    void request
      .startVerification('m.sas.v1')
      .then(bindVerifier)
      .catch(() =>
        set({ stage: 'error', busy: false, error: 'Não consegui começar a comparação.' }),
      );
  }
}

function adopt(next: VerificationRequest, stage: VerificationStage) {
  detach();
  request = next;
  set({
    ...IDLE,
    stage,
    isSelf: next.isSelfVerification,
    otherUserId: next.otherUserId,
  });
  next.on(VerificationRequestEvent.Change, onRequestChange);
  onRequestChange();
}

/** Liga a escuta de pedidos recebidos (outro aparelho meu ou um contato). */
export function attachVerification(client: MatrixClient): void {
  client.on(CryptoEvent.VerificationRequestReceived, (incoming: VerificationRequest) => {
    if (state.stage === 'emoji' || state.stage === 'waiting') return; // já tem uma em curso
    adopt(incoming, 'incoming');
  });
}

export async function verifyOwnDevices(client: MatrixClient): Promise<void> {
  const crypto = client.getCrypto();
  if (!crypto || state.busy) return;
  set({ busy: true, error: '' });
  try {
    adopt(await crypto.requestOwnUserVerification(), 'waiting');
  } catch {
    set({ stage: 'error', busy: false, error: 'Não consegui chamar o outro aparelho agora.' });
  }
}

export async function verifyContact(
  client: MatrixClient,
  userId: string,
  roomId: string,
): Promise<void> {
  const crypto = client.getCrypto();
  if (!crypto || state.busy) return;
  set({ busy: true, error: '' });
  try {
    adopt(await crypto.requestVerificationDM(userId, roomId), 'waiting');
  } catch {
    set({ stage: 'error', busy: false, error: 'Não consegui chamar essa pessoa agora.' });
  }
}

export async function acceptVerification(): Promise<void> {
  if (!request || state.busy) return;
  set({ busy: true, stage: 'waiting' });
  try {
    await request.accept();
    onRequestChange();
  } catch {
    set({ stage: 'error', busy: false, error: 'Não consegui aceitar agora.' });
  }
}

export async function confirmEmojiMatch(): Promise<void> {
  if (!sas || state.busy) return;
  set({ busy: true });
  try {
    await sas.confirm();
  } catch {
    set({ stage: 'error', busy: false, error: 'A confirmação não fechou. Tente de novo.' });
  }
}

export function reportEmojiMismatch(): void {
  set({ error: 'Os desenhos não batem. Pode ser outra pessoa no meio: não confirme.' });
  sas?.mismatch();
  set({ stage: 'cancelled', busy: false });
}

export function cancelVerification(): void {
  void request?.cancel({ reason: 'Cancelado pelo usuário' }).catch(() => {});
  detach();
  set({ ...IDLE });
}

export function dismissVerification(): void {
  detach();
  set({ ...IDLE });
}

export function resetVerification(): void {
  detach();
  state = IDLE;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useVerificationState(): VerificationState {
  return useSyncExternalStore(
    subscribe,
    () => state,
    () => state,
  );
}

/** Já confirmei a identidade dessa pessoa (comparando os desenhos)? */
export async function isPersonVerified(client: MatrixClient, userId: string): Promise<boolean> {
  try {
    return (await client.getCrypto()?.getUserVerificationStatus(userId))?.isVerified() ?? false;
  } catch {
    return false;
  }
}
